import { useEffect, useState, type ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Activity,
  ArrowUpRight,
  Check,
  Cloud,
  Code2,
  Cpu,
  Database,
  GitBranch,
  HardDrive,
  Shield,
  Sparkles,
  Terminal,
  Wifi,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { heroMorphCopy } from "@/lib/siteCopy";

type StageId = "software" | "cloud" | "ai" | "mobile" | "support";

const STAGES: StageId[] = ["software", "cloud", "ai", "mobile", "support"];
const STAGE_MS = 5200;
const ease = [0.22, 1, 0.36, 1] as const;

/** Matches What We Solve card backgrounds */
const STAGE_COLORS: Record<
  StageId,
  {
    bg: string;
    surface: string;
    ink: string;
    muted: string;
    border: string;
    accent: string;
    dark: boolean;
  }
> = {
  software: {
    bg: "#F3EFEA",
    surface: "#FFFFFF",
    ink: "#171717",
    muted: "rgba(23,23,23,0.5)",
    border: "rgba(23,23,23,0.1)",
    accent: "#0550AE",
    dark: false,
  },
  cloud: {
    bg: "#1A1A1A",
    surface: "#242424",
    ink: "#FAFAFA",
    muted: "rgba(250,250,250,0.55)",
    border: "rgba(255,255,255,0.12)",
    accent: "#34D399",
    dark: true,
  },
  ai: {
    bg: "#F2E8F8",
    surface: "#FFFFFF",
    ink: "#171717",
    muted: "rgba(23,23,23,0.5)",
    border: "rgba(23,23,23,0.1)",
    accent: "#7C3AED",
    dark: false,
  },
  mobile: {
    bg: "#E8EEF5",
    surface: "#FFFFFF",
    ink: "#171717",
    muted: "rgba(23,23,23,0.5)",
    border: "rgba(23,23,23,0.1)",
    accent: "#2563EB",
    dark: false,
  },
  support: {
    bg: "#FFF4DE",
    surface: "#FFFFFF",
    ink: "#171717",
    muted: "rgba(23,23,23,0.5)",
    border: "rgba(23,23,23,0.1)",
    accent: "#059669",
    dark: false,
  },
};

const stageFade = {
  initial: { opacity: 0, y: 18, scale: 0.985 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.55, ease },
  },
  exit: {
    opacity: 0,
    y: -12,
    scale: 0.99,
    transition: { duration: 0.32, ease: [0.4, 0, 1, 1] as const },
  },
};

function StageFrame({
  stage,
  children,
}: {
  stage: StageId;
  children: ReactNode;
}) {
  const lang = useSiteLanguage();
  const title = heroMorphCopy[lang][stage];
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 overflow-visible px-1 py-0.5 sm:gap-3 sm:px-2 lg:gap-3.5">
      <div className="relative flex min-h-0 w-full flex-1 items-center justify-center overflow-visible lg:max-h-none">
        {children}
      </div>
      <motion.p
        className="shrink-0 rounded-full border border-black/[0.06] bg-white/80 px-3 py-1 text-[10px] font-medium tracking-[-0.01em] text-neutral-600 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-white/10 dark:text-neutral-300 sm:text-xs"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.4, ease }}
      >
        {title}
      </motion.p>
    </div>
  );
}

function SoftCard({
  children,
  className = "",
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 14, filter: "blur(4px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ delay, duration: 0.55, ease }}
    >
      {children}
    </motion.div>
  );
}

/* ─── Software ─────────────────────────────────────────────── */

function SoftwareStage() {
  const lines = [
    {
      n: 12,
      parts: [
        { t: "import", k: true },
        { t: " { " },
        { t: "auth", fn: true },
        { t: ", " },
        { t: "db", fn: true },
        { t: " } from " },
        { t: '"@ddc/core"', str: true },
      ],
    },
    { n: 13, parts: [{ t: "" }] },
    {
      n: 14,
      parts: [
        { t: "export", k: true },
        { t: " " },
        { t: "async", k: true },
        { t: " " },
        { t: "function", k: true },
        { t: " " },
        { t: "Platform", fn: true },
        { t: "() {" },
      ],
    },
    {
      n: 15,
      parts: [
        { t: "  " },
        { t: "const", k: true },
        { t: " session = " },
        { t: "await", k: true },
        { t: " " },
        { t: "auth", fn: true },
        { t: ".verify()" },
      ],
    },
    {
      n: 16,
      parts: [
        { t: "  " },
        { t: "const", k: true },
        { t: " graph = " },
        { t: "await", k: true },
        { t: " " },
        { t: "db", fn: true },
        { t: ".query(session)" },
      ],
    },
    {
      n: 17,
      parts: [
        { t: "  " },
        { t: "if", k: true },
        { t: " (!graph.ok) " },
        { t: "throw", k: true },
        { t: " " },
        { t: "graph", fn: true },
        { t: ".error" },
      ],
    },
    {
      n: 18,
      parts: [
        { t: "  " },
        { t: "return", k: true },
        { t: " " },
        { t: "<Shell", tag: true },
        { t: " data={graph} />" },
      ],
    },
    { n: 19, parts: [{ t: "}" }] },
  ];

  const partClass = (p: {
    k?: boolean;
    fn?: boolean;
    tag?: boolean;
    str?: boolean;
  }) =>
    "k" in p && p.k
      ? "text-[#0550AE] dark:text-sky-400"
      : "fn" in p && p.fn
        ? "text-[#6F42C1] dark:text-violet-400"
        : "tag" in p && p.tag
          ? "text-[#116329] dark:text-emerald-400"
          : "str" in p && p.str
            ? "text-[#0A3069] dark:text-sky-300"
            : "text-black/80 dark:text-white/80";

  return (
    <motion.div key="software" className="absolute inset-0" {...stageFade}>
      <StageFrame stage="software">
        <div className="relative mx-auto flex h-full w-full max-w-[min(100%,24rem)] flex-col justify-center sm:max-w-[26rem] lg:max-w-[28rem]">
          <SoftCard className="relative flex max-h-full w-full flex-col overflow-hidden rounded-2xl border border-black/[0.08] bg-white shadow-[0_20px_50px_rgba(0,0,0,0.1)] dark:border-white/10 dark:bg-[#1a1a1a]">
            {/* Title bar */}
            <div className="flex h-9 shrink-0 items-center gap-2 border-b border-black/[0.06] bg-[#FAFAF8]/90 px-3 dark:border-white/10 dark:bg-white/[0.04] sm:h-10 sm:px-3.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
              <div className="ml-1.5 flex items-center gap-1 rounded-md bg-black/[0.04] px-2 py-0.5 dark:bg-white/10">
                <Code2 className="h-3 w-3 text-black/40 dark:text-white/50" />
                <span className="font-mono text-[10px] text-black/55 dark:text-white/60">
                  platform.tsx
                </span>
              </div>
              <span className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] text-black/35 dark:text-white/40">
                <GitBranch className="h-3 w-3" />
                main
              </span>
            </div>

            {/* File tabs */}
            <div className="flex shrink-0 gap-0.5 overflow-hidden border-b border-black/[0.05] bg-[#F7F6F4] px-2 dark:border-white/[0.06] dark:bg-white/[0.03]">
              {[
                { name: "platform.tsx", on: true },
                { name: "auth.ts", on: false },
                { name: "shell.tsx", on: false },
              ].map((tab) => (
                <div
                  key={tab.name}
                  className={cn(
                    "flex items-center gap-1.5 px-2.5 py-1.5 font-mono text-[10px]",
                    tab.on
                      ? "border-b-2 border-[#0550AE] bg-white text-black/70 dark:bg-[#1a1a1a] dark:text-white/70"
                      : "text-black/35 dark:text-white/35",
                  )}
                >
                  {tab.name}
                </div>
              ))}
            </div>

            {/* Editor + gutter */}
            <div className="relative min-h-0 flex-1 space-y-[2px] overflow-hidden bg-gradient-to-b from-[#F3EFEA]/80 to-white px-3 py-2.5 font-mono text-[10.5px] leading-[1.65] dark:from-white/[0.03] dark:to-[#1a1a1a] sm:px-3.5 sm:py-3 sm:text-[11.5px]">
              <motion.div
                className="pointer-events-none absolute inset-y-2 left-[2.6rem] w-px bg-[#0550AE]/15 dark:bg-sky-400/20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
              />
              {lines.map((line, i) => (
                <motion.div
                  key={line.n}
                  className="relative flex gap-2.5"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.35, ease }}
                >
                  <span className="w-5 shrink-0 select-none text-right text-black/20 dark:text-white/25">
                    {line.n}
                  </span>
                  <span className="min-w-0 truncate">
                    {line.parts.map((p, j) => (
                      <span key={j} className={partClass(p)}>
                        {p.t}
                      </span>
                    ))}
                  </span>
                </motion.div>
              ))}
              <motion.div
                className="flex gap-2.5"
                animate={{ opacity: [1, 0.2, 1] }}
                transition={{ duration: 1.05, repeat: Infinity }}
              >
                <span className="w-5" />
                <span className="inline-block h-[1.15em] w-[2px] rounded-full bg-[#0550AE] dark:bg-sky-400" />
              </motion.div>
            </div>

            {/* Terminal panel */}
            <div className="shrink-0 border-t border-black/[0.06] bg-[#1e1e1e] px-3 py-2 dark:border-white/10">
              <div className="mb-1.5 flex items-center gap-3 text-[9px] text-white/40">
                <span className="font-medium text-emerald-400/90">TERMINAL</span>
                <span>PROBLEMS</span>
                <span>OUTPUT</span>
              </div>
              <p className="font-mono text-[10px] leading-relaxed text-emerald-400/90">
                <span className="text-white/35">$</span> ddc build --watch
              </p>
              <p className="font-mono text-[10px] text-white/55">
                ✓ compiled in{" "}
                <span className="text-emerald-400">1.24s</span> · 0 errors
              </p>
            </div>

            <div className="flex shrink-0 items-center justify-between border-t border-black/[0.06] bg-black/[0.02] px-3 py-1.5 text-[10px] text-black/40 dark:border-white/10 dark:bg-white/[0.03] dark:text-white/40">
              <span className="inline-flex items-center gap-1.5">
                <Terminal className="h-3 w-3" />
                TypeScript · React 19
              </span>
              <span className="font-mono">Ln 18 · UTF-8</span>
            </div>
          </SoftCard>

          <SoftCard
            delay={0.4}
            className="absolute -right-1 top-2 z-10 hidden items-center gap-2 rounded-full border border-black/[0.08] bg-white px-2.5 py-1 text-[10px] font-medium text-black/55 shadow-md sm:flex lg:-right-3"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            48 tests passing
          </SoftCard>

          <SoftCard
            delay={0.55}
            className="absolute -left-1 bottom-16 z-10 hidden rounded-lg border border-black/[0.08] bg-white px-2.5 py-1.5 shadow-md sm:block lg:-left-4"
          >
            <p className="font-mono text-[9px] text-black/35">coverage</p>
            <p className="font-mono text-[11px] font-medium text-emerald-700">
              94.2%
            </p>
          </SoftCard>
        </div>
      </StageFrame>
    </motion.div>
  );
}

/* ─── Cloud ────────────────────────────────────────────────── */

function CloudStage() {
  const regions = [
    { id: "iad1", load: 62, ms: "11ms" },
    { id: "sfo1", load: 44, ms: "14ms" },
    { id: "fra1", load: 71, ms: "9ms" },
    { id: "bom1", load: 38, ms: "12ms" },
    { id: "sin1", load: 55, ms: "13ms" },
    { id: "syd1", load: 29, ms: "18ms" },
  ];

  const pipeline = [
    { label: "Build", ok: true },
    { label: "Test", ok: true },
    { label: "Secure", ok: true },
    { label: "Deploy", ok: true },
  ];

  return (
    <motion.div key="cloud" className="absolute inset-0" {...stageFade}>
      <StageFrame stage="cloud">
        <div className="mx-auto flex h-full w-full max-w-[min(100%,24rem)] flex-col justify-center gap-1.5 sm:max-w-[26rem] sm:gap-2.5 lg:max-w-[28rem]">
          <SoftCard className="rounded-2xl border border-white/[0.1] bg-[#1A1A1A] p-2.5 shadow-[0_16px_40px_rgba(0,0,0,0.28)] sm:p-3.5">
            <div className="flex items-start gap-2.5 sm:gap-3">
              <div className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.06] sm:h-10 sm:w-10">
                <Cloud className="h-4 w-4 text-white/75 sm:h-5 sm:w-5" strokeWidth={1.5} />
                <motion.span
                  className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-emerald-400"
                  animate={{ scale: [1, 1.25, 1], opacity: [1, 0.7, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[13px] font-semibold tracking-[-0.02em] text-white sm:text-sm">
                    Edge Network
                  </p>
                  <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-400">
                    Healthy
                  </span>
                </div>
                <p className="mt-0.5 text-[10px] text-white/45 sm:text-[12px]">
                  24 regions · 12ms median TTFB · 99.99% SLA
                </p>
                <div className="mt-2 grid grid-cols-4 gap-1 sm:mt-2.5 sm:grid-cols-6 sm:gap-1.5">
                  {regions.map((r, i) => (
                    <div
                      key={r.id}
                      className={cn(
                        "rounded-md border border-white/[0.08] bg-black/35 px-1 py-1 sm:px-1.5 sm:py-1.5",
                        i >= 4 && "hidden sm:block",
                      )}
                    >
                      <p className="font-mono text-[8px] text-white/45 sm:text-[9px]">
                        {r.id}
                      </p>
                      <div className="mt-1 h-1 overflow-hidden rounded-full bg-white/10">
                        <motion.div
                          className="h-full rounded-full bg-emerald-400/80"
                          initial={{ width: 0 }}
                          animate={{ width: `${r.load}%` }}
                          transition={{
                            delay: 0.25 + i * 0.05,
                            duration: 0.65,
                            ease,
                          }}
                        />
                      </div>
                      <p className="mt-1 hidden font-mono text-[8px] text-emerald-400/80 sm:block">
                        {r.ms}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </SoftCard>

          {/* CI pipeline - desktop/tablet */}
          <SoftCard
            delay={0.12}
            className="hidden rounded-2xl border border-white/[0.1] bg-[#1A1A1A] px-3 py-2.5 shadow-[0_12px_32px_rgba(0,0,0,0.22)] sm:block"
          >
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] font-semibold text-white/80">
                Production pipeline
              </p>
              <span className="font-mono text-[9px] text-emerald-400">
                #1842 passed
              </span>
            </div>
            <div className="flex items-center gap-1">
              {pipeline.map((step, i) => (
                <div key={step.label} className="flex min-w-0 flex-1 items-center gap-1">
                  <div className="min-w-0 flex-1 rounded-md border border-emerald-500/25 bg-emerald-500/10 px-1.5 py-1.5 text-center">
                    <Check className="mx-auto h-3 w-3 text-emerald-400" strokeWidth={2.5} />
                    <p className="mt-0.5 truncate text-[8px] font-medium text-emerald-300/90">
                      {step.label}
                    </p>
                  </div>
                  {i < pipeline.length - 1 && (
                    <span className="h-px w-1.5 shrink-0 bg-emerald-500/40 sm:w-2" />
                  )}
                </div>
              ))}
            </div>
          </SoftCard>

          <div className="grid grid-cols-2 gap-1.5 sm:gap-2.5">
            {[
              {
                icon: Shield,
                title: "API Gateway",
                meta: "JWT · WAF · rate limits",
                value: "4ms p50",
              },
              {
                icon: HardDrive,
                title: "Compute",
                meta: "Autoscaled · multi-AZ",
                value: "12 nodes",
              },
              {
                icon: Database,
                title: "Postgres",
                meta: "Primary + 2 replicas",
                value: "PITR on",
              },
              {
                icon: Activity,
                title: "Observability",
                meta: "Traces · metrics · logs",
                value: "Live",
              },
            ].map((card, i) => {
              const Icon = card.icon;
              return (
                <SoftCard
                  key={card.title}
                  delay={0.18 + i * 0.07}
                  className={cn(
                    "rounded-2xl border border-white/[0.1] bg-[#1A1A1A] p-2 shadow-[0_12px_32px_rgba(0,0,0,0.22)] sm:p-3",
                    i >= 2 && "hidden sm:block",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-white/[0.06] sm:h-7 sm:w-7">
                      <Icon className="h-3.5 w-3.5 text-white/70" strokeWidth={1.5} />
                    </div>
                    <p className="text-[11px] font-semibold tracking-[-0.02em] text-white sm:text-[13px]">
                      {card.title}
                    </p>
                  </div>
                  <p className="mt-1 text-[9px] text-white/40 sm:mt-1.5 sm:text-[11px]">
                    {card.meta}
                  </p>
                  <p className="mt-0.5 font-mono text-[10px] text-emerald-400/90 sm:mt-1 sm:text-[11px]">
                    {card.value}
                  </p>
                </SoftCard>
              );
            })}
          </div>
        </div>
      </StageFrame>
    </motion.div>
  );
}

/* ─── AI ───────────────────────────────────────────────────── */

function AiStage() {
  const [typed, setTyped] = useState("");
  const full = "Idle miles down 18% · projected savings $4.2k this week.";

  useEffect(() => {
    let i = 0;
    setTyped("");
    const id = window.setInterval(() => {
      i += 1;
      setTyped(full.slice(0, i));
      if (i >= full.length) window.clearInterval(id);
    }, 20);
    return () => window.clearInterval(id);
  }, []);

  return (
    <motion.div key="ai" className="absolute inset-0" {...stageFade}>
      <StageFrame stage="ai">
        <SoftCard className="mx-auto flex w-full max-w-[min(100%,24rem)] flex-col overflow-hidden rounded-2xl border border-black/[0.08] bg-white shadow-[0_20px_50px_rgba(124,58,237,0.12)] dark:border-white/10 dark:bg-[#1a1a1a] sm:max-w-[26rem] lg:max-w-[28rem]">
          <div className="flex shrink-0 items-center justify-between border-b border-black/[0.06] px-3 py-2.5 dark:border-white/10 sm:px-4">
            <div className="flex min-w-0 items-center gap-2">
              <div className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F2E8F8]">
                <Sparkles className="h-3.5 w-3.5 text-violet-700" strokeWidth={1.5} />
                <motion.span
                  className="absolute inset-0 rounded-lg bg-violet-400/20"
                  animate={{ opacity: [0.2, 0.55, 0.2] }}
                  transition={{ duration: 2.4, repeat: Infinity }}
                />
              </div>
              <div className="min-w-0">
                <p className="truncate text-[12px] font-semibold tracking-[-0.02em] text-black dark:text-white">
                  Logistics Agent
                </p>
                <p className="truncate text-[10px] text-black/40 dark:text-white/45">
                  gpt-class · tools enabled
                </p>
              </div>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[9px] font-medium text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Live
            </span>
          </div>

          <div className="space-y-2 bg-gradient-to-b from-[#F2E8F8]/55 to-white px-3 py-2.5 dark:from-violet-500/10 dark:to-[#1a1a1a] sm:px-4 sm:py-3">
            <SoftCard
              delay={0.1}
              className="ml-auto max-w-[88%] rounded-2xl rounded-br-md bg-neutral-900 px-3 py-2 text-[11px] leading-snug text-white shadow-sm"
            >
              Optimize 12 delivery routes. Prefer lower fuel cost.
            </SoftCard>

            <SoftCard
              delay={0.28}
              className="max-w-[94%] space-y-2 rounded-2xl rounded-bl-md border border-black/[0.06] bg-white p-2.5 shadow-sm dark:border-white/10 dark:bg-white/[0.06]"
            >
              <div className="flex flex-wrap items-center gap-1">
                {["route.graph", "fuel.model"].map((tool) => (
                  <span
                    key={tool}
                    className="inline-flex items-center gap-1 rounded-md bg-[#F2E8F8] px-1.5 py-0.5 font-mono text-[8px] text-violet-700 dark:bg-violet-500/15 dark:text-violet-300"
                  >
                    <Cpu className="h-2.5 w-2.5" />
                    {tool}
                  </span>
                ))}
              </div>
              <p className="min-h-[2.2em] text-[11px] leading-snug text-black/80 dark:text-white/80">
                {typed}
                <motion.span
                  className="ml-0.5 inline-block h-[1em] w-[2px] translate-y-[2px] bg-violet-600 align-middle"
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                />
              </p>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { l: "Routes", v: "12→9" },
                  { l: "Fuel", v: "-18%" },
                  { l: "ETA", v: "+0.4h" },
                ].map((stat, i) => (
                  <motion.div
                    key={stat.l}
                    className="rounded-md border border-violet-100 bg-[#F2E8F8]/80 px-1.5 py-1.5 dark:border-violet-500/20 dark:bg-violet-500/15"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 + i * 0.08 }}
                  >
                    <p className="text-[8px] uppercase tracking-[0.06em] text-black/40 dark:text-white/45">
                      {stat.l}
                    </p>
                    <p className="font-mono text-[10px] font-medium text-black dark:text-white">
                      {stat.v}
                    </p>
                  </motion.div>
                ))}
              </div>
            </SoftCard>
          </div>

          <div className="shrink-0 border-t border-black/[0.06] px-3 py-2 dark:border-white/10 sm:px-4">
            <div className="flex items-center gap-2 rounded-lg border border-black/[0.08] bg-[#FAFAFA] px-2.5 py-1.5 dark:border-white/10 dark:bg-white/[0.04]">
              <span className="flex-1 truncate text-[11px] text-black/30 dark:text-white/35">
                Ask the agent…
              </span>
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-neutral-900 text-white">
                <ArrowUpRight className="h-3 w-3" strokeWidth={2} />
              </span>
            </div>
          </div>
        </SoftCard>
      </StageFrame>
    </motion.div>
  );
}

/* ─── Mobile / App Development ─────────────────────────────── */

function MobileStage() {
  const bars = [32, 48, 40, 62, 45, 78, 58, 70, 52, 84, 66, 90];

  return (
    <motion.div key="mobile" className="absolute inset-0" {...stageFade}>
      <StageFrame stage="mobile">
        <div className="relative flex w-full items-center justify-center">
          <SoftCard
            delay={0.18}
            className="absolute left-[2%] top-[8%] z-20 hidden rounded-xl border border-black/[0.06] bg-white px-2.5 py-1.5 shadow-[0_10px_28px_rgba(0,0,0,0.08)] sm:block lg:left-[8%]"
          >
            <p className="text-[8px] text-black/40">Push open</p>
            <p className="text-[12px] font-semibold tracking-tight text-black">
              64.2%
            </p>
          </SoftCard>
          <SoftCard
            delay={0.32}
            className="absolute bottom-[10%] right-[2%] z-20 hidden rounded-xl border border-black/[0.06] bg-white px-2.5 py-1.5 shadow-[0_10px_28px_rgba(0,0,0,0.08)] sm:block lg:right-[8%]"
          >
            <div className="flex items-center gap-1.5">
              <Wifi className="h-3 w-3 text-emerald-600" />
              <p className="text-[10px] font-medium text-black/70">5G · 18ms</p>
            </div>
          </SoftCard>

          <SoftCard className="relative h-[19.5rem] w-[10.25rem] shrink-0 sm:h-[21.5rem] sm:w-[11.5rem] lg:h-[23rem] lg:w-[12.25rem]">
            <span
              className="absolute -left-[2px] top-[17%] h-6 w-[2px] rounded-l-[1px] bg-neutral-700"
              aria-hidden
            />
            <span
              className="absolute -left-[2px] top-[30%] h-9 w-[2px] rounded-l-[1px] bg-neutral-700"
              aria-hidden
            />
            <span
              className="absolute -right-[2px] top-[24%] h-11 w-[2px] rounded-r-[1px] bg-neutral-700"
              aria-hidden
            />

            <div className="relative flex h-full w-full flex-col overflow-hidden rounded-[1.85rem] bg-[#0B0B0C] p-[3px] shadow-[0_28px_64px_rgba(15,23,42,0.3)] ring-1 ring-white/10 sm:rounded-[2rem]">
              <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[1.65rem] bg-[#F2F4F8] sm:rounded-[1.8rem]">
                <div className="absolute left-1/2 top-2 z-30 flex h-[1.05rem] w-[4.5rem] -translate-x-1/2 items-center justify-end rounded-full bg-black px-2">
                  <span className="h-[5px] w-[5px] rounded-full bg-[#1c1c2e] ring-[0.5px] ring-neutral-600" />
                </div>

                <div className="relative z-20 flex items-center justify-between px-3.5 pb-0.5 pt-3">
                  <span className="text-[10px] font-semibold tracking-tight text-neutral-900">
                    9:41
                  </span>
                  <div className="flex items-center gap-[3px] text-neutral-900">
                    <svg viewBox="0 0 16 12" className="h-2 w-3" aria-hidden>
                      <rect x="0" y="7" width="2.2" height="5" rx="0.4" fill="currentColor" />
                      <rect x="3.5" y="5" width="2.2" height="7" rx="0.4" fill="currentColor" />
                      <rect x="7" y="3" width="2.2" height="9" rx="0.4" fill="currentColor" />
                      <rect
                        x="10.5"
                        y="1"
                        width="2.2"
                        height="11"
                        rx="0.4"
                        fill="currentColor"
                        opacity="0.3"
                      />
                    </svg>
                    <Wifi className="h-2.5 w-2.5" strokeWidth={2.5} />
                    <span className="relative ml-px h-[8px] w-[16px] rounded-[2px] border border-neutral-900/85">
                      <span className="absolute inset-[1px] right-[2.5px] rounded-[0.5px] bg-emerald-500" />
                      <span className="absolute -right-[2px] top-1/2 h-[3.5px] w-[1.25px] -translate-y-1/2 rounded-r-[0.5px] bg-neutral-900/85" />
                    </span>
                  </div>
                </div>

                <div className="relative z-10 flex min-h-0 flex-1 flex-col gap-2 overflow-hidden px-2.5 pb-1 pt-0.5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[9px] font-medium text-neutral-500">
                        Good morning
                      </p>
                      <p className="text-[12px] font-semibold tracking-tight text-neutral-900">
                        Analytics
                      </p>
                    </div>
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-[9px] font-bold text-white">
                      D
                    </div>
                  </div>

                  <div className="rounded-[1rem] bg-white p-2.5 shadow-[0_6px_18px_rgba(15,23,42,0.06)] ring-1 ring-black/[0.035]">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-[9px] text-neutral-500">Active users</p>
                        <motion.p
                          className="mt-0.5 text-lg font-semibold tracking-[-0.04em] text-neutral-950"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                        >
                          24,182
                        </motion.p>
                      </div>
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[8px] font-semibold text-emerald-700">
                        <Zap className="h-2.5 w-2.5" />
                        +12%
                      </span>
                    </div>
                    <div className="mt-2 flex h-9 items-end gap-[2.5px]">
                      {bars.map((h, i) => (
                        <motion.span
                          key={i}
                          className="flex-1 rounded-[2px] bg-gradient-to-t from-blue-600 to-sky-400"
                          initial={{ height: 4 }}
                          animate={{ height: `${Math.max(14, h * 0.45)}%` }}
                          transition={{
                            delay: 0.15 + i * 0.03,
                            duration: 0.45,
                            ease,
                          }}
                          style={{ maxHeight: "100%" }}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-1.5">
                    <div className="rounded-[0.85rem] bg-white px-2 py-1.5 shadow-[0_3px_12px_rgba(15,23,42,0.04)] ring-1 ring-black/[0.035]">
                      <p className="text-[8px] text-neutral-500">Crash-free</p>
                      <p className="text-[11px] font-semibold text-neutral-900">
                        99.97%
                      </p>
                    </div>
                    <div className="rounded-[0.85rem] bg-white px-2 py-1.5 shadow-[0_3px_12px_rgba(15,23,42,0.04)] ring-1 ring-black/[0.035]">
                      <p className="text-[8px] text-neutral-500">p95</p>
                      <p className="text-[11px] font-semibold text-neutral-900">
                        42ms
                      </p>
                    </div>
                  </div>

                  <div className="mt-auto space-y-1.5">
                    <div className="flex h-8 items-center justify-center rounded-xl bg-neutral-950 text-[10px] font-semibold text-white">
                      Open dashboard
                    </div>
                    <div className="flex items-center justify-around rounded-2xl bg-white/95 py-1 ring-1 ring-black/[0.04]">
                      {["Home", "Stats", "Alerts", "You"].map((tab, i) => (
                        <span
                          key={tab}
                          className={cn(
                            "rounded-lg px-2 py-1 text-[8px] font-medium",
                            i === 1
                              ? "bg-blue-50 text-blue-700"
                              : "text-neutral-400",
                          )}
                        >
                          {tab}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-center pb-1 pt-0.5">
                  <span className="h-[3px] w-20 rounded-full bg-neutral-900/80" />
                </div>
              </div>
            </div>
          </SoftCard>
        </div>
      </StageFrame>
    </motion.div>
  );
}

/* ─── Support ──────────────────────────────────────────────── */

function SupportStage() {
  const items = [
    { label: "Uptime (30d)", value: "99.98%", bar: 99 },
    { label: "Open incidents", value: "0", bar: 100 },
    { label: "Median response", value: "12m", bar: 86 },
    { label: "CSAT", value: "4.9 / 5", bar: 96 },
    { label: "MTTR", value: "8m", bar: 88 },
    { label: "SLA met", value: "100%", bar: 100 },
  ];
  const feed = [
    { t: "Deploy rolled out · prod", s: "2m ago" },
    { t: "Health check passed · all regions", s: "5m ago" },
    { t: "Backup completed · encrypted", s: "18m ago" },
    { t: "Certificate renewed · api.ddc", s: "1h ago" },
  ];

  return (
    <motion.div key="support" className="absolute inset-0" {...stageFade}>
      <StageFrame stage="support">
        <div className="mx-auto flex w-full max-w-[min(100%,24rem)] flex-col gap-1.5 sm:max-w-[26rem] sm:gap-2.5 lg:max-w-[28rem]">
          <SoftCard className="flex items-center gap-2.5 rounded-2xl border border-black/[0.07] bg-white px-3 py-2.5 shadow-[0_12px_36px_rgba(0,0,0,0.08)] dark:border-white/10 dark:bg-[#1a1a1a] sm:px-4 sm:py-3">
            <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/12 sm:h-9 sm:w-9">
              <Check className="h-3.5 w-3.5 text-emerald-600 sm:h-4 sm:w-4" strokeWidth={2.5} />
              <motion.span
                className="absolute inset-0 rounded-full border border-emerald-400/40"
                animate={{ scale: [1, 1.35], opacity: [0.6, 0] }}
                transition={{ duration: 1.8, repeat: Infinity }}
              />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-semibold tracking-[-0.02em] text-black dark:text-white sm:text-[13px]">
                All systems operational
              </p>
              <p className="text-[10px] text-black/40 dark:text-white/45 sm:text-[11px]">
                14 services · updated just now
              </p>
            </div>
            <Activity className="h-4 w-4 text-emerald-600/70" />
          </SoftCard>

          {/* Status strip - sm+ */}
          <SoftCard
            delay={0.1}
            className="hidden rounded-2xl border border-black/[0.07] bg-white px-3 py-2.5 shadow-[0_10px_28px_rgba(0,0,0,0.06)] dark:border-white/10 dark:bg-[#1a1a1a] sm:block"
          >
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] font-semibold text-black/70 dark:text-white/70">
                Service health
              </p>
              <span className="font-mono text-[9px] text-emerald-600">
                14/14 up
              </span>
            </div>
            <div className="flex gap-1">
              {Array.from({ length: 14 }).map((_, i) => (
                <motion.span
                  key={i}
                  className="h-6 flex-1 rounded-sm bg-emerald-500/80"
                  initial={{ opacity: 0, scaleY: 0.4 }}
                  animate={{ opacity: 1, scaleY: 1 }}
                  transition={{ delay: 0.15 + i * 0.03, duration: 0.35 }}
                  style={{ originY: 1 }}
                />
              ))}
            </div>
          </SoftCard>

          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 sm:gap-2.5">
            {items.map((item, i) => (
              <SoftCard
                key={item.label}
                delay={0.12 + i * 0.05}
                className={cn(
                  "rounded-2xl border border-black/[0.07] bg-white px-2.5 py-2 shadow-[0_10px_28px_rgba(0,0,0,0.06)] dark:border-white/10 dark:bg-[#1a1a1a] sm:px-3 sm:py-2.5",
                  i >= 4 && "hidden sm:block",
                )}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="truncate text-[10px] text-black/45 dark:text-white/45">
                    {item.label}
                  </span>
                  <span className="shrink-0 font-mono text-[11px] font-medium tabular-nums text-black dark:text-white">
                    {item.value}
                  </span>
                </div>
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/10">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400"
                    initial={{ width: 0 }}
                    animate={{ width: `${item.bar}%` }}
                    transition={{ delay: 0.2 + i * 0.05, duration: 0.6, ease }}
                  />
                </div>
              </SoftCard>
            ))}
          </div>

          <SoftCard
            delay={0.35}
            className="rounded-2xl border border-black/[0.07] bg-white px-3 py-2 shadow-[0_10px_28px_rgba(0,0,0,0.06)] dark:border-white/10 dark:bg-[#1a1a1a] sm:px-4 sm:py-2.5"
          >
            <div className="mb-1.5 flex items-center justify-between">
              <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-black/35 dark:text-white/40">
                Activity
              </p>
              <span className="text-[9px] text-black/35 dark:text-white/40">
                Live feed
              </span>
            </div>
            <div className="space-y-1.5">
              {feed.map((f, i) => (
                <motion.div
                  key={f.t}
                  className={cn(
                    "flex items-center gap-2",
                    i >= 2 && "hidden sm:flex",
                  )}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.45 + i * 0.08 }}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  <span className="flex-1 truncate text-[11px] text-black/70 dark:text-white/70">
                    {f.t}
                  </span>
                  <span className="shrink-0 font-mono text-[9px] text-black/35 dark:text-white/40">
                    {f.s}
                  </span>
                </motion.div>
              ))}
            </div>
          </SoftCard>
        </div>
      </StageFrame>
    </motion.div>
  );
}

function renderStage(stage: StageId) {
  switch (stage) {
    case "software":
      return <SoftwareStage />;
    case "cloud":
      return <CloudStage />;
    case "ai":
      return <AiStage />;
    case "mobile":
      return <MobileStage />;
    case "support":
      return <SupportStage />;
  }
}

export function HeroLogoMorph({
  className = "",
  showControls = true,
}: {
  className?: string;
  showControls?: boolean;
}) {
  const lang = useSiteLanguage();
  const reduceMotion = useReducedMotion();
  const [stageIndex, setStageIndex] = useState(0);
  const [progressKey, setProgressKey] = useState(0);
  const stage = STAGES[stageIndex]!;

  useEffect(() => {
    if (reduceMotion) return;
    const id = window.setInterval(() => {
      setStageIndex((i) => (i + 1) % STAGES.length);
      setProgressKey((k) => k + 1);
    }, STAGE_MS);
    return () => window.clearInterval(id);
  }, [reduceMotion, progressKey]);

  const goTo = (i: number) => {
    setStageIndex(i);
    setProgressKey((k) => k + 1);
  };

  return (
    <div
      className={cn(
        "relative mx-auto flex h-full w-full flex-col overflow-visible",
        className,
      )}
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="relative min-h-[26rem] w-full flex-1 overflow-visible sm:min-h-[28rem] lg:min-h-0">
        <AnimatePresence mode="wait">{renderStage(stage)}</AnimatePresence>
      </div>

      {showControls && (
        <div className="mt-3 hidden flex-col items-center gap-2.5 sm:flex">
          <div className="flex justify-center gap-1.5">
            {STAGES.map((s, i) => {
              const active = i === stageIndex;
              const color = STAGE_COLORS[s].bg;
              return (
                <button
                  key={s}
                  type="button"
                  aria-label={heroMorphCopy[lang][s]}
                  onClick={() => goTo(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    active ? "w-6" : "w-1.5 opacity-45 hover:opacity-80"
                  }`}
                  style={{
                    backgroundColor: color,
                    boxShadow: active
                      ? `0 0 0 1px ${
                          STAGE_COLORS[s].dark
                            ? "rgba(255,255,255,0.25)"
                            : "rgba(0,0,0,0.12)"
                        }`
                      : undefined,
                  }}
                />
              );
            })}
          </div>

          {!reduceMotion && (
            <div className="h-[2px] w-24 overflow-hidden rounded-full bg-black/10 dark:bg-white/15">
              <motion.div
                key={progressKey}
                className="h-full rounded-full"
                style={{ backgroundColor: STAGE_COLORS[stage].accent }}
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: STAGE_MS / 1000, ease: "linear" }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
